import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Card, Button, Form, Badge, Modal, Alert, Dropdown, Nav, Table, Pagination } from 'react-bootstrap';
import { CheckCircle, XCircle, Plus, ThreeDots } from 'react-bootstrap-icons';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../../assets/sass/2-components/_shift-calendar.scss';
import PageLayout from '../../layouts/PageLayout';
import { useBranch, useBranchAPI } from '../../hooks';
import { shiftAPI, employeeAPI } from '../../services';
import { getErrorMessage } from '../../utils/apiClient';
import { toast } from 'react-toastify';

// Setup moment localizer
const localizer = momentLocalizer(moment);

export default function ShiftManagementPage() {
  const { selectedBranch } = useBranch();
  const { withBranch } = useBranchAPI();


  const [shifts, setShifts] = useState([]);
  const [shiftSlots, setShiftSlots] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('calendar');

  // Low registration shifts states
  const [lowRegShifts, setLowRegShifts] = useState([]);
  const [lowRegLoading, setLowRegLoading] = useState(false);
  const [lowRegPage, setLowRegPage] = useState(1);
  const [lowRegMeta, setLowRegMeta] = useState({ total: 0, page: 1, limit: 100, total_pages: 1 });

  // Calendar states
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [employeeFilter, setEmployeeFilter] = useState('');

  // Ref to track previous values to prevent unnecessary API calls
  const prevValuesRef = useRef({});

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    employee_id: '',
    start_time: '',
    end_time: '',
    shift_type: 'NORMAL',
    difficulty_level: 1,
    bonus_points: 0,
    min_employees: 1,
    max_employees: 1,
  });

  const loadShiftSlots = useCallback(
    async (date) => {
      try {
        const dateStr = moment(date).format('YYYY-MM-DD');
        const response = await withBranch(shiftAPI.getShiftSlots, selectedBranch?.id, dateStr);
        setShiftSlots(response.data || response);
      } catch (err) {
        console.error('Error loading shift slots:', err);
        toast.error('Không thể tải slot ca làm việc');
      }
    },
    [selectedBranch?.id, withBranch],
  );

  // Load data
  useEffect(() => {
    const currentValues = {
      selectedBranch: selectedBranch?.id,
      currentDate: currentDate.toISOString(),
      view,
      employeeFilter,
    };

    // Check if values have actually changed
    const prevValues = prevValuesRef.current;
    const hasChanged = Object.keys(currentValues).some((key) => prevValues[key] !== currentValues[key]);

    if (!hasChanged) {
      console.log('Values unchanged, skipping API calls');
      return;
    }

    console.log('Values changed, making API calls:', currentValues);
    prevValuesRef.current = currentValues;

    // Call API functions directly to avoid dependency issues
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load shifts
        let dateFrom, dateTo;
        if (view === 'month') {
          dateFrom = moment(currentDate).startOf('month').format('YYYY-MM-DD');
          dateTo = moment(currentDate).endOf('month').format('YYYY-MM-DD');
        } else if (view === 'week') {
          dateFrom = moment(currentDate).startOf('isoWeek').format('YYYY-MM-DD');
          dateTo = moment(currentDate).endOf('week').format('YYYY-MM-DD');
        } else if (view === 'day') {
          dateFrom = moment(currentDate).format('YYYY-MM-DD');
          dateTo = moment(currentDate).format('YYYY-MM-DD');
        }

        const shiftParams = {
          branch_id: selectedBranch?.id,
          employee_id: employeeFilter || '',
          date_from: dateFrom,
          date_to: dateTo,
          page: 1,
          limit: 100,
          status: 'PENDING',
        };

        const employeeParams = {
          page: 1,
          limit: 100,
          status: 'ACTIVE',
        };

        console.log('Loading shifts with params:', shiftParams);
        console.log('Loading employees with params:', employeeParams);

        const [shiftsResponse, employeesResponse] = await Promise.all([
          withBranch(shiftAPI.getShifts, shiftParams),
          withBranch(employeeAPI.getEmployees, employeeParams),
        ]);

        setShifts(shiftsResponse.data || shiftsResponse);
        setEmployees(employeesResponse.data || employeesResponse);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Không thể tải dữ liệu');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedBranch?.id, currentDate, view, employeeFilter, withBranch]);

  const handleCreateShift = async () => {
    try {
      setLoading(true);

      const shiftData = {
        ...formData,
        branch_id: selectedBranch?.id,
        current_user_id: 'admin', // TODO: Get from auth context
      };

      await withBranch(shiftAPI.createShift, shiftData);

      toast.success('Tạo ca làm việc thành công!');
      setShowCreateModal(false);
      resetForm();
      // Trigger data reload by updating a dependency
      setCurrentDate(new Date());
    } catch (err) {
      console.error('Error creating shift:', err);
      toast.error('Không thể tạo ca làm việc');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveShift = async (shiftId) => {
    try {
      await withBranch(shiftAPI.approveShift, shiftId, 'admin'); // TODO: Get from auth context
      toast.success('Duyệt ca làm việc thành công!');
      // Trigger data reload by updating a dependency
      setCurrentDate(new Date());
      // Reload low registration shifts if on that tab
      if (activeTab === 'low-reg') {
        loadLowRegShifts(lowRegPage);
      }
    } catch (err) {
      console.error('Error approving shift:', err);
      // #190823 — cùng họ lỗi với AdminShiftReviewPage: chữ cứng nuốt mất lý do thật của backend
      // (vd chặn ca thu ngân của NV còn trong tháng đầu tập việc).
      toast.error(getErrorMessage(err) || 'Không thể duyệt ca làm việc');
    }
  };

  const promptRejectShift = (shiftId) => {
    const reason = window.prompt('Nhập lý do từ chối ca làm việc:');
    if (!reason?.trim()) return;
    handleRejectShift(shiftId, reason.trim());
  };

  const handleRejectShift = async (shiftId, reason) => {
    try {
      await withBranch(shiftAPI.rejectShift, shiftId, 'admin', reason); // TODO: Get from auth context
      toast.success('Từ chối ca làm việc thành công!');
      // Trigger data reload by updating a dependency
      setCurrentDate(new Date());
      // Reload low registration shifts if on that tab
      if (activeTab === 'low-reg') {
        loadLowRegShifts(lowRegPage);
      }
    } catch (err) {
      console.error('Error rejecting shift:', err);
      toast.error(getErrorMessage(err) || 'Không thể từ chối ca làm việc');
    }
  };

  const handleSelectSlot = async (slotInfo) => {
    setSelectedDate(slotInfo.start);
    await loadShiftSlots(slotInfo.start);
    setShowSlotModal(true);
  };

  const handleSelectEvent = (event) => {
    setSelectedSlot(event);
    setShowSlotModal(true);
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      start_time: '',
      end_time: '',
      shift_type: 'NORMAL',
      difficulty_level: 1,
      bonus_points: 0,
      min_employees: 1,
      max_employees: 1,
    });
  };

  // Load low registration shifts
  const loadLowRegShifts = useCallback(
    async (page = 1) => {
      if (!selectedBranch?.id) return;
      setLowRegLoading(true);
      try {
        const response = await withBranch(shiftAPI.getShiftsGroupedByTemplate, {
          branch_id: selectedBranch.id,
          status: 'PENDING',
          page,
          limit: 100,
        });
        const data = response.data || [];
        const meta = response.meta || { total: 0, page: 1, limit: 100, total_pages: 1 };
        setLowRegShifts(data);
        setLowRegMeta(meta);
        setLowRegPage(page);
      } catch (err) {
        console.error('Error loading low registration shifts:', err);
        toast.error('Không thể tải danh sách ca có ít đăng ký');
      } finally {
        setLowRegLoading(false);
      }
    },
    [selectedBranch?.id, withBranch],
  );

  useEffect(() => {
    if (activeTab === 'low-reg') {
      loadLowRegShifts(1);
    }
  }, [activeTab, selectedBranch?.id, loadLowRegShifts]);

  const getStatusBadge = (status) => {
    const statusMap = {
      PENDING: { variant: 'warning', text: 'Chờ duyệt' },
      APPROVED: { variant: 'success', text: 'Đã duyệt' },
      COMPLETED: { variant: 'info', text: 'Hoàn thành' },
      CANCELLED: { variant: 'danger', text: 'Đã hủy' },
    };

    const statusInfo = statusMap[status] || { variant: 'secondary', text: status };
    return <Badge bg={statusInfo.variant}>{statusInfo.text}</Badge>;
  };

  const getShiftTypeBadge = (type) => {
    const typeMap = {
      NORMAL: { variant: 'primary', text: 'Thường' },
      WEEKEND: { variant: 'warning', text: 'Cuối tuần' },
      NIGHT: { variant: 'dark', text: 'Ca đêm' },
      HOLIDAY: { variant: 'danger', text: 'Lễ' },
      PEAK_HOUR: { variant: 'info', text: 'Giờ cao điểm' },
    };

    const typeInfo = typeMap[type] || { variant: 'secondary', text: type };
    return <Badge bg={typeInfo.variant}>{typeInfo.text}</Badge>;
  };

  const formatDate = (date) => {
    return moment(date).format('DD/MM/YYYY');
  };

  const formatTime = (dateTime) => {
    return moment(dateTime).format('HH:mm');
  };

  // Convert shifts to calendar events
  const getCalendarEvents = () => {
    if (!shifts || !Array.isArray(shifts)) return [];

    return shifts.map((shift) => ({
      id: shift.id,
      title: `${shift.employee?.full_name || 'N/A'} - ${getShiftTypeBadge(shift.shift_type)}`,
      start: new Date(shift.start_time),
      end: new Date(shift.end_time),
      resource: shift,
      className: `shift-event ${shift.status.toLowerCase()}`,
    }));
  };

  // Custom event component
  const EventComponent = ({ event }) => {
    const shift = event.resource;
    return (
      <div className="shift-event-content">
        <div className="shift-employee">{shift.employee?.full_name || 'N/A'}</div>
        <div className="shift-time">
          {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
        </div>
        <div className="shift-status">{getStatusBadge(shift.status)}</div>
        {shift.status === 'PENDING' && (
          <div className="shift-actions">
            <Dropdown>
              <Dropdown.Toggle
                variant="link"
                size="sm"
                className="p-0"
                onClick={(e) => e.stopPropagation()}
              >
                <ThreeDots size={12} />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handleApproveShift(shift.id)}>
                  <CheckCircle className="me-2 text-success" />
                  Duyệt
                </Dropdown.Item>
                <Dropdown.Item onClick={() => promptRejectShift(shift.id)}>
                  <XCircle className="me-2 text-danger" />
                  Từ chối
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}
      </div>
    );
  };

  // Get event color based on status
  const getEventColor = (status) => {
    const colorMap = {
      PENDING: '#ffc107', // warning
      APPROVED: '#198754', // success
      COMPLETED: '#0dcaf0', // info
      CANCELLED: '#dc3545', // danger
    };
    return colorMap[status] || '#6c757d'; // secondary
  };


  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header">
          <div className="mc-page-header-title">
            <h1>Quản lý ca làm việc</h1>
            <p>Quản lý và duyệt ca làm việc của nhân viên</p>
          </div>
          <div className="mc-page-header-actions">
            <Button
              variant="primary"
              onClick={() => setShowCreateModal(true)}
              disabled={loading}
            >
              <Plus className="me-2" />
              Tạo ca mới
            </Button>
          </div>
        </div>

        {/* Tabs Navigation */}
        <Card className="mc-card mb-4">
          <Card.Body className="p-0">
            <Nav
              variant="tabs"
              className="border-bottom"
            >
              <Nav.Item>
                <Nav.Link
                  active={activeTab === 'calendar'}
                  onClick={() => setActiveTab('calendar')}
                  style={{ cursor: 'pointer' }}
                >
                  Lịch ca làm việc
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  active={activeTab === 'low-reg'}
                  onClick={() => setActiveTab('low-reg')}
                  style={{ cursor: 'pointer' }}
                >
                  Ca có ít đăng ký ({lowRegMeta.total || 0})
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Body>
        </Card>

        {/* Calendar Controls */}
        {activeTab === 'calendar' && (
          <Card className="mc-card mb-4">
            <Card.Body>
              <Row className="align-items-center">
                <Col md={6}>
                  <div className="d-flex align-items-center gap-3">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setView('month')}
                      active={view === 'month'}
                    >
                      Tháng
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setView('week')}
                      active={view === 'week'}
                    >
                      Tuần
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setView('day')}
                      active={view === 'day'}
                    >
                      Ngày
                    </Button>
                  </div>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-0">
                    <Form.Select
                      size="sm"
                      value={employeeFilter}
                      onChange={(e) => setEmployeeFilter(e.target.value)}
                    >
                      <option value="">Tất cả nhân viên</option>
                      {employees.map((emp) => (
                        <option
                          key={emp.id}
                          value={emp.id}
                        >
                          {emp.full_name} ({emp.username})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setCurrentDate(new Date())}
                    className="w-100"
                  >
                    Hôm nay
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Statistics */}
        {activeTab === 'calendar' && (
          <Row className="mb-4">
            <Col md={3}>
              <Card className="mc-card">
                <Card.Body className="text-center">
                  <div className="text-warning mb-2">
                    <i
                      className="bi bi-clock"
                      style={{ fontSize: '24px' }}
                    ></i>
                  </div>
                  <h4>{shifts.filter((s) => s.status === 'PENDING').length}</h4>
                  <p className="text-muted mb-0">Chờ duyệt</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="mc-card">
                <Card.Body className="text-center">
                  <div className="text-success mb-2">
                    <i
                      className="bi bi-check-circle"
                      style={{ fontSize: '24px' }}
                    ></i>
                  </div>
                  <h4>{shifts.filter((s) => s.status === 'APPROVED').length}</h4>
                  <p className="text-muted mb-0">Đã duyệt</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="mc-card">
                <Card.Body className="text-center">
                  <div className="text-info mb-2">
                    <i
                      className="bi bi-check2-all"
                      style={{ fontSize: '24px' }}
                    ></i>
                  </div>
                  <h4>{shifts.filter((s) => s.status === 'COMPLETED').length}</h4>
                  <p className="text-muted mb-0">Hoàn thành</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="mc-card">
                <Card.Body className="text-center">
                  <div className="text-primary mb-2">
                    <i
                      className="bi bi-people"
                      style={{ fontSize: '24px' }}
                    ></i>
                  </div>
                  <h4>{shifts.length}</h4>
                  <p className="text-muted mb-0">Tổng ca</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Calendar */}
        {activeTab === 'calendar' && (
          <Card className="mc-card">
            <Card.Body className="p-0">
              {error && (
                <Alert
                  variant="danger"
                  className="m-3"
                >
                  {error}
                </Alert>
              )}

              {loading ? (
                <div className="text-center py-5">
                  <div
                    className="spinner-border"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div
                  className="shift-calendar"
                  style={{ blockSize: '600px' }}
                >
                  <BigCalendar
                    localizer={localizer}
                    events={getCalendarEvents()}
                    startAccessor="start"
                    endAccessor="end"
                    view={view}
                    views={['month', 'week', 'day']}
                    date={currentDate}
                    onNavigate={setCurrentDate}
                    onView={setView}
                    onSelectSlot={handleSelectSlot}
                    onSelectEvent={handleSelectEvent}
                    selectable
                    popup
                    components={{
                      event: EventComponent,
                    }}
                    messages={{
                      next: 'Tiếp',
                      previous: 'Trước',
                      today: 'Hôm nay',
                      month: 'Tháng',
                      week: 'Tuần',
                      day: 'Ngày',
                      agenda: 'Lịch trình',
                      date: 'Ngày',
                      time: 'Thời gian',
                      event: 'Sự kiện',
                      noEventsInRange: 'Không có ca làm việc nào trong khoảng thời gian này.',
                      showMore: (total) => `+${total} ca khác`,
                    }}
                    eventPropGetter={(event) => ({
                      style: {
                        backgroundColor: getEventColor(event.resource.status),
                        borderColor: getEventColor(event.resource.status),
                        color: 'white',
                        borderRadius: '4px',
                        border: 'none',
                        fontSize: '12px',
                      },
                    })}
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Low Registration Shifts Tab */}
        {activeTab === 'low-reg' && (
          <Card className="mc-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Danh sách ca có ít đăng ký</h5>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => loadLowRegShifts(lowRegPage)}
                  disabled={lowRegLoading}
                >
                  Làm mới
                </Button>
              </div>

              {lowRegLoading ? (
                <div className="text-center py-5">
                  <div
                    className="spinner-border"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : lowRegShifts.length === 0 ? (
                <Alert variant="info">Không có ca nào cần duyệt.</Alert>
              ) : (
                <>
                  <Table
                    responsive
                    hover
                  >
                    <thead>
                      <tr>
                        <th>Ca làm việc</th>
                        <th>Nhân viên</th>
                        <th>Thời gian</th>
                        <th>Đăng ký</th>
                        <th>Trạng thái</th>
                        <th>Thao tác</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lowRegShifts.map((shift) => (
                        <tr key={shift.id}>
                          <td>
                            <div>
                              <strong>{shift.template_name || 'N/A'}</strong>
                            </div>
                          </td>
                          <td>{shift.employee_name || 'N/A'}</td>
                          <td>
                            <div>
                              {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                            </div>
                            <small className="text-muted">{formatDate(shift.start_time)}</small>
                          </td>
                          <td>
                            <Badge bg={shift.registration_count < shift.max_employees ? 'warning' : 'success'}>
                              {shift.registration_count}/{shift.max_employees}
                            </Badge>
                          </td>
                          <td>{getStatusBadge(shift.status)}</td>
                          <td>
                            {shift.status === 'PENDING' && (
                              <Dropdown>
                                <Dropdown.Toggle
                                  variant="link"
                                  size="sm"
                                  className="p-0"
                                >
                                  <ThreeDots size={16} />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => handleApproveShift(shift.id)}>
                                    <CheckCircle className="me-2 text-success" />
                                    Duyệt
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => promptRejectShift(shift.id)}>
                                    <XCircle className="me-2 text-danger" />
                                    Từ chối
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {lowRegMeta.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-3">
                      <Pagination>
                        <Pagination.First
                          disabled={lowRegPage === 1}
                          onClick={() => loadLowRegShifts(1)}
                        />
                        <Pagination.Prev
                          disabled={lowRegPage === 1}
                          onClick={() => loadLowRegShifts(lowRegPage - 1)}
                        />
                        {Array.from({ length: Math.min(5, lowRegMeta.total_pages) }, (_, i) => {
                          let pageNum;
                          if (lowRegMeta.total_pages <= 5) {
                            pageNum = i + 1;
                          } else if (lowRegPage <= 3) {
                            pageNum = i + 1;
                          } else if (lowRegPage >= lowRegMeta.total_pages - 2) {
                            pageNum = lowRegMeta.total_pages - 4 + i;
                          } else {
                            pageNum = lowRegPage - 2 + i;
                          }
                          return (
                            <Pagination.Item
                              key={pageNum}
                              active={pageNum === lowRegPage}
                              onClick={() => loadLowRegShifts(pageNum)}
                            >
                              {pageNum}
                            </Pagination.Item>
                          );
                        })}
                        <Pagination.Next
                          disabled={lowRegPage >= lowRegMeta.total_pages}
                          onClick={() => loadLowRegShifts(lowRegPage + 1)}
                        />
                        <Pagination.Last
                          disabled={lowRegPage >= lowRegMeta.total_pages}
                          onClick={() => loadLowRegShifts(lowRegMeta.total_pages)}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Create Shift Modal */}
        <Modal
          show={showCreateModal}
          onHide={() => setShowCreateModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Tạo ca làm việc mới</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nhân viên *</Form.Label>
                  <Form.Select
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    required
                  >
                    <option value="">Chọn nhân viên</option>
                    {employees.map((emp) => (
                      <option
                        key={emp.id}
                        value={emp.id}
                      >
                        {emp.full_name} ({emp.username})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Loại ca</Form.Label>
                  <Form.Select
                    value={formData.shift_type}
                    onChange={(e) => setFormData({ ...formData, shift_type: e.target.value })}
                  >
                    <option value="NORMAL">Thường</option>
                    <option value="WEEKEND">Cuối tuần</option>
                    <option value="NIGHT">Ca đêm</option>
                    <option value="HOLIDAY">Lễ</option>
                    <option value="PEAK_HOUR">Giờ cao điểm</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Thời gian bắt đầu *</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Thời gian kết thúc *</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Độ khó</Form.Label>
                  <Form.Select
                    value={formData.difficulty_level}
                    onChange={(e) => setFormData({ ...formData, difficulty_level: parseInt(e.target.value) })}
                  >
                    <option value={1}>Dễ</option>
                    <option value={2}>Trung bình</option>
                    <option value={3}>Khó</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Điểm thưởng</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={formData.bonus_points}
                    onChange={(e) => setFormData({ ...formData, bonus_points: parseInt(e.target.value) || 0 })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Số nhân viên tối đa</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={formData.max_employees}
                    onChange={(e) => setFormData({ ...formData, max_employees: parseInt(e.target.value) || 1 })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Hủy
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateShift}
              disabled={loading}
            >
              {loading ? 'Đang tạo...' : 'Tạo ca'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Slot Management Modal */}
        <Modal
          show={showSlotModal}
          onHide={() => setShowSlotModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Quản lý slot ca - {selectedDate && formatDate(selectedDate)}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedSlot ? (
              <div>
                <h6>Chi tiết ca làm việc</h6>
                <Row className="mb-3">
                  <Col md={6}>
                    <strong>Nhân viên:</strong> {selectedSlot.employee?.full_name || 'N/A'}
                  </Col>
                  <Col md={6}>
                    <strong>Trạng thái:</strong> {getStatusBadge(selectedSlot.status)}
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={6}>
                    <strong>Thời gian:</strong> {formatTime(selectedSlot.start_time)} -{' '}
                    {formatTime(selectedSlot.end_time)}
                  </Col>
                  <Col md={6}>
                    <strong>Loại ca:</strong> {getShiftTypeBadge(selectedSlot.shift_type)}
                  </Col>
                </Row>
                {selectedSlot.status === 'PENDING' && (
                  <div className="d-flex gap-2">
                    <Button
                      variant="success"
                      onClick={() => {
                        handleApproveShift(selectedSlot.id);
                        setShowSlotModal(false);
                      }}
                    >
                      <CheckCircle className="me-2" />
                      Duyệt ca
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => {
                        promptRejectShift(selectedSlot.id);
                        setShowSlotModal(false);
                      }}
                    >
                      <XCircle className="me-2" />
                      Từ chối
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <h6>Slot ca có sẵn</h6>
                {shiftSlots.length > 0 ? (
                  <div className="list-group">
                    {shiftSlots.map((slot, index) => (
                      <div
                        key={index}
                        className="list-group-item"
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>
                              {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                            </strong>
                            <br />
                            <small className="text-muted">
                              {slot.registered_employees || 0}/{slot.max_employees} nhân viên
                            </small>
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                start_time: slot.start_time,
                                end_time: slot.end_time,
                              });
                              setShowSlotModal(false);
                              setShowCreateModal(true);
                            }}
                          >
                            Thêm nhân viên
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Alert variant="info">Không có slot ca nào trong ngày này. Bạn có thể tạo ca mới.</Alert>
                )}
                <div className="mt-3">
                  <Button
                    variant="primary"
                    onClick={() => {
                      setShowSlotModal(false);
                      setShowCreateModal(true);
                    }}
                  >
                    <Plus className="me-2" />
                    Tạo ca mới
                  </Button>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowSlotModal(false)}
            >
              Đóng
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </PageLayout>
  );
}
